<template>
  <div class="h-full overflow-y-scroll">
    <div class="p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <h3 class="text-2xl font-bold">Shopify</h3>

      <div v-if="providerAuthorized && storeConfigured" class="flex flex-col sm:flex-row gap-2">
        <div class="border border-primary rounded-full overflow-hidden flex bg-white text-sm">
          <button
            v-for="option in tabs"
            :key="option.value"
            class="py-1 px-3 whitespace-nowrap flex items-center justify-center gap-2"
            :class="tab === option.value ? 'bg-primary text-white' : 'text-primary'"
            @click="tab = option.value"
          >
            <span>{{ option.label }}</span>
            <span
              class="w-5 h-5 rounded-full inline-flex items-center justify-center text-xs"
              :class="tab === option.value ? 'bg-white text-primary' : 'bg-primary text-white'"
            >
              {{ countForTab(option.value) }}
            </span>
          </button>
        </div>
        <button
          class="border border-primary rounded text-primary font-bold py-1 px-3"
          @click="reconfiguring ? cancelReconfiguration() : beginReconfiguration()"
        >
          {{ reconfiguring ? 'Cerrar configuración' : 'Configuración' }}
        </button>
      </div>
    </div>

    <div v-if="providerAuthorized && !loading && (!storeConfigured || reconfiguring)" class="mx-4 mb-4 border border-gray-300 rounded bg-white p-4 lg:p-6">
      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div>
          <h4 class="font-bold text-lg">
            {{ storeConfigured ? 'Configuración de Shopify' : 'Conectar Shopify' }}
          </h4>
          <p class="text-sm text-gray-600 mt-1">
            Shopify enviará cada orden nueva a esta bandeja para que la mapees antes de crear el pedido.
          </p>
        </div>
      </div>

      <ol class="mt-5 space-y-4 text-sm">
        <li class="flex gap-3">
          <span class="bg-primary text-white rounded-full w-6 h-6 flex-none flex items-center justify-center font-bold">1</span>
          <div>
            En Shopify abre <strong>Configuración → Notificaciones → Webhooks</strong> y selecciona
            <strong>Crear webhook</strong>.
          </div>
        </li>
        <li class="flex gap-3">
          <span class="bg-primary text-white rounded-full w-6 h-6 flex-none flex items-center justify-center font-bold">2</span>
          <div>
            Usa el evento <strong>Creación de orden</strong>, formato <strong>JSON</strong> y la versión estable más reciente de la API.
          </div>
        </li>
        <li>
          <div class="flex gap-3">
            <span class="bg-primary text-white rounded-full w-6 h-6 flex-none flex items-center justify-center font-bold">3</span>
            <p>Pega esta URL como destino:</p>
          </div>
          <div class="mt-3 border border-gray-200 rounded p-4">
            <label for="shopify-webhook-url" class="block text-gray-700 text-sm font-bold mb-3">
              URL del webhook
            </label>
            <div class="flex flex-col sm:flex-row gap-2">
              <input
                id="shopify-webhook-url"
                :value="webhookUrl"
                readonly
                class="bg-gray-50 border border-gray-300 rounded py-2 px-3 flex-auto min-w-0 w-full"
              />
              <button
                type="button"
                class="bg-primary text-white rounded font-bold py-2 px-4 whitespace-nowrap"
                @click="copyWebhook"
              >
                Copiar webhook
              </button>
            </div>
          </div>
        </li>
        <li>
          <div class="flex gap-3">
            <span class="bg-primary text-white rounded-full w-6 h-6 flex-none flex items-center justify-center font-bold">4</span>
            <div>
              Guarda el webhook, copia su clave de firma y finaliza la conexión abajo. Después usa
              <strong>Enviar prueba</strong>; la primera orden válida aparecerá en <strong>Pendientes</strong>.
            </div>
          </div>
          <div class="mt-3 border border-gray-200 rounded p-4 space-y-4">
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2">
                Dominio permanente de Shopify
              </label>
              <p class="text-sm text-gray-600 mb-3">
                En Shopify abre <strong>Configuración → Dominios</strong> y copia el dominio que termina en
                <strong>.myshopify.com</strong>. No uses tu dominio público personalizado.
              </p>
              <div v-if="storeConfigured && !editingStoreDomain" class="flex gap-2">
                <input
                  :value="storeDomain"
                  type="text"
                  disabled
                  aria-label="Dominio permanente guardado"
                  class="border border-gray-300 rounded py-2 px-3 flex-auto min-w-0 bg-gray-100 text-gray-600 cursor-not-allowed"
                />
                <button
                  type="button"
                  class="border border-primary rounded text-primary font-bold py-2 px-4"
                  @click="beginDomainEdit"
                >
                  Editar
                </button>
              </div>
              <div v-else-if="storeConfigured" class="flex gap-2">
                <input
                  v-model.trim="storeDomain"
                  type="text"
                  autocomplete="off"
                  placeholder="mi-tienda.myshopify.com"
                  class="border border-gray-300 rounded py-2 px-3 flex-auto min-w-0"
                />
                <button
                  type="button"
                  class="border border-gray-300 text-gray-700 rounded font-bold py-2 px-4"
                  :disabled="saving"
                  @click="cancelDomainEdit"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  class="bg-primary text-white rounded font-bold py-2 px-4 disabled:opacity-50"
                  :disabled="saving || !storeDomain"
                  @click="connectStore"
                >
                  {{ saving ? 'Guardando…' : 'Guardar' }}
                </button>
              </div>
              <input
                v-else
                v-model.trim="storeDomain"
                type="text"
                autocomplete="off"
                placeholder="mi-tienda.myshopify.com"
                class="border border-gray-300 rounded py-2 px-3 w-full"
              />
            </div>

            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2">
                Clave de firma de webhooks
              </label>
              <p class="text-sm text-gray-600 mb-3">
                Después de guardar el webhook en Shopify, copia la clave con la que la tienda firma sus webhooks.
              </p>
              <div v-if="storeConfigured && !editingWebhookSecret" class="flex gap-2">
                <input
                  value="••••••••"
                  type="text"
                  disabled
                  aria-label="Clave de firma guardada"
                  class="border border-gray-300 rounded py-2 px-3 flex-auto min-w-0 bg-gray-100 text-gray-600 cursor-not-allowed"
                />
                <button
                  type="button"
                  class="border border-primary rounded text-primary font-bold py-2 px-4"
                  @click="beginSecretEdit"
                >
                  Editar
                </button>
              </div>
              <div v-else-if="storeConfigured" class="flex gap-2">
                <input
                  v-model.trim="webhookSecret"
                  type="password"
                  autocomplete="new-password"
                  placeholder="Nueva clave de firma"
                  class="border border-gray-300 rounded py-2 px-3 flex-auto min-w-0"
                />
                <button
                  type="button"
                  class="border border-gray-300 text-gray-700 rounded font-bold py-2 px-4"
                  :disabled="saving"
                  @click="cancelSecretEdit"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  class="bg-primary text-white rounded font-bold py-2 px-4 disabled:opacity-50"
                  :disabled="saving || !webhookSecret"
                  @click="connectStore"
                >
                  {{ saving ? 'Guardando…' : 'Guardar' }}
                </button>
              </div>
              <input
                v-else
                v-model.trim="webhookSecret"
                type="password"
                autocomplete="new-password"
                placeholder="Clave de firma"
                class="border border-gray-300 rounded py-2 px-3 w-full"
              />
            </div>

            <div v-if="!storeConfigured" class="flex justify-end">
              <button
                class="bg-primary text-white rounded font-bold py-2 px-4 whitespace-nowrap disabled:opacity-50"
                :disabled="saving || !storeDomain || !webhookSecret"
                @click="connectStore"
              >
                {{ saving ? 'Conectando…' : 'Finalizar conexión' }}
              </button>
            </div>
          </div>
        </li>
      </ol>

    </div>

    <div v-if="providerAuthorized && !loading && storeConfigured && !reconfiguring && filteredOrders.length === 0" class="m-4 border border-gray-300 rounded bg-white p-6 text-center text-gray-500">
      No hay órdenes en esta sección.
    </div>

    <div v-else-if="providerAuthorized && !loading && storeConfigured && !reconfiguring" class="grid grid-cols-1 lg:grid-cols-12 gap-4 px-4 pb-4">
      <div class="lg:col-span-4 space-y-2">
        <button
          v-for="order in filteredOrders"
          :key="order.id"
          class="w-full text-left border rounded bg-white p-4"
          :class="selected && selected.id === order.id ? 'border-primary ring-1 ring-primary' : 'border-gray-300'"
          @click="selectOrder(order)"
        >
          <div class="flex justify-between gap-2">
            <span class="font-bold">{{ order.order_number }}</span>
            <span class="text-sm">{{ money(order.total, order.currency) }}</span>
          </div>
          <p class="text-sm truncate mt-1">{{ order.customer_name || order.email || 'Sin cliente' }}</p>
          <div class="flex justify-between mt-2 text-xs text-gray-500">
            <span>{{ date(order.shopify_created_at) }}</span>
            <span :class="orderStatusClass(order.status)">
              {{ orderStatusLabel(order.status) }}
            </span>
          </div>
        </button>
      </div>

      <div v-if="selected" class="lg:col-span-8 border border-gray-300 rounded bg-white overflow-hidden">
        <div class="p-4 border-b flex justify-between items-start gap-4">
          <div>
            <h4 class="text-xl font-bold">{{ selected.order_number }}</h4>
          </div>
          <router-link
            v-if="selected.pedido"
            class="text-primary underline whitespace-nowrap"
            :to="`/proveedor/${route.params.proveedor_id}/orden/${selected.pedido}`"
          >
            Pedido #{{ selected.pedido }}
          </router-link>
        </div>

        <div class="p-4 space-y-6">
          <section>
            <h5 class="font-bold mb-2">Orden de Shopify</h5>
            <div class="border border-gray-200 rounded-lg bg-white overflow-hidden text-sm">
              <div class="grid grid-cols-1 md:grid-cols-2">
                <div class="md:border-r border-gray-200">
                  <h6 class="bg-gray-50 border-b border-gray-200 px-4 py-2 font-semibold text-gray-800">
                    Contacto
                  </h6>
                  <div class="text-gray-700 px-4 py-3 space-y-1">
                    <p>{{ selected.customer_name || contact.nombre || 'Sin contacto' }}</p>
                    <p v-if="contact.telefono">{{ contact.telefono }}</p>
                    <p class="break-words">{{ selected.email || 'Sin correo' }}</p>
                  </div>
                </div>

                <div class="border-t md:border-t-0 border-gray-200">
                  <h6 class="bg-gray-50 border-b border-gray-200 px-4 py-2 font-semibold text-gray-800">
                    Entrega
                  </h6>
                  <div class="text-gray-700 px-4 py-3 space-y-1">
                    <p v-if="contact.nombre">{{ contact.nombre }}</p>
                    <p class="leading-relaxed">{{ deliveryAddressLine(selected) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h5 class="font-bold mb-2">Cliente PickPack</h5>
            <div
              v-if="suggestedClient && !readonly"
              class="border border-primary rounded p-3 mb-3 bg-background-light flex items-center justify-between gap-3"
            >
              <div class="min-w-0">
                <p class="font-bold truncate">{{ suggestedClient.nombre }}</p>
                <p class="text-xs text-gray-500 truncate">{{ suggestedClient.contacto_correo }}</p>
              </div>
              <button
                type="button"
                class="flex-shrink-0 bg-primary hover:bg-primary-dark disabled:opacity-60 text-white font-bold py-2 px-4 rounded"
                :disabled="suggestionAssigned"
                @click="assignSuggestedClient"
              >
                {{ suggestionAssigned ? "Asignado" : "Asignar" }}
              </button>
            </div>
            <select
              v-model="clientSelection"
              :disabled="readonly"
              class="disabled:opacity-60 border border-gray-300 rounded w-full py-2 px-3"
              @change="selectClientOption"
            >
              <option :value="null" disabled>Selecciona un cliente</option>
              <option value="new">Nuevo cliente</option>
              <option disabled>──────────</option>
              <option v-for="client in clients" :key="client.id" :value="client.id">
                {{ client.nombre }}
              </option>
            </select>

            <div v-if="clientSelection === 'new' && !readonly" class="border border-gray-200 rounded p-4 mt-3 space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Nombre comercial</label>
                  <input v-model="newClient.nombre" class="border rounded w-full py-2 px-3" />
                </div>
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Nombre de contacto</label>
                  <input v-model="newClient.contacto.nombre" class="border rounded w-full py-2 px-3" />
                </div>
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Teléfono</label>
                  <input v-model="newClient.contacto.telefono" type="tel" class="border rounded w-full py-2 px-3" />
                </div>
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Correo</label>
                  <input v-model="newClient.contacto.correo" type="email" class="border rounded w-full py-2 px-3" />
                </div>
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Calle</label>
                  <input v-model="newClient.direccion.calle" class="border rounded w-full py-2 px-3" />
                </div>
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Exterior</label>
                  <input v-model="newClient.direccion.exterior" class="border rounded w-full py-2 px-3" />
                </div>
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Interior</label>
                  <input v-model="newClient.direccion.interior" class="border rounded w-full py-2 px-3" />
                </div>
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Colonia / ciudad</label>
                  <input v-model="newClient.direccion.colonia" class="border rounded w-full py-2 px-3" />
                </div>
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Código postal</label>
                  <input v-model="newClient.direccion.codigo" class="border rounded w-full py-2 px-3" />
                </div>
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Estado</label>
                  <input v-model="newClient.direccion.estado" class="border rounded w-full py-2 px-3" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h5 class="font-bold mb-2">Productos</h5>
            <div class="border border-gray-200 rounded overflow-x-auto">
              <table class="w-full text-sm text-left">
                <thead class="border-b bg-gray-50">
                  <tr>
                    <th class="py-3 px-3">Shopify</th>
                    <th class="py-3 px-3">Producto PickPack</th>
                    <th class="py-3 px-3">Cantidad</th>
                    <th class="py-3 px-3">Precio</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in selected.shopify_order_items" :key="item.id" class="border-b last:border-b-0">
                    <td class="py-3 px-3 min-w-48">
                      <p class="font-medium">{{ item.title }}</p>
                    </td>
                    <td class="py-3 px-3 min-w-56">
                      <select
                        v-model="item.producto"
                        :disabled="readonly"
                        class="disabled:opacity-60 border border-gray-300 rounded w-full py-2 px-2"
                      >
                        <option :value="null" disabled>Mapear producto</option>
                        <option v-for="product in products" :key="product.id" :value="product.id">
                          {{ product.nombre }}
                        </option>
                      </select>
                      <label v-if="item.variant_id && !readonly" class="flex items-center gap-2 mt-2 text-xs text-gray-600">
                        <input v-model="item.remember_mapping" type="checkbox" />
                        Recordar para próximas órdenes
                      </label>
                    </td>
                    <td class="py-3 px-3 w-28">
                      <input
                        v-model.number="item.final_quantity"
                        :disabled="readonly"
                        type="number"
                        min="1"
                        class="disabled:opacity-60 border border-gray-300 rounded w-full py-2 px-2"
                      />
                    </td>
                    <td class="py-3 px-3 w-32">
                      <input
                        v-model.number="item.final_price"
                        :disabled="readonly"
                        type="number"
                        min="0"
                        step="0.01"
                        class="disabled:opacity-60 border border-gray-300 rounded w-full py-2 px-2"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <label class="flex items-center gap-2">
              <input v-model="selected.require_factura" :disabled="readonly" type="checkbox" />
              Requiere factura
            </label>
          </section>

          <section>
            <label class="block text-gray-700 text-sm font-bold mb-2">Instrucciones</label>
            <textarea
              v-model="selected.instrucciones"
              :disabled="readonly"
              rows="3"
              class="disabled:opacity-60 border rounded w-full py-2 px-3"
            ></textarea>
          </section>

          <div v-if="selected.error_message" class="border border-red rounded p-3 text-red text-sm">
            {{ selected.error_message }}
          </div>
        </div>

        <div v-if="!readonly" class="p-4 border-t flex flex-col-reverse sm:flex-row gap-2 justify-between">
          <button class="border border-red text-red rounded font-bold py-2 px-4" :disabled="saving" @click="discardOrder">
            Descartar
          </button>
          <button
            class="bg-primary text-white rounded font-bold py-2 px-4 disabled:opacity-50"
            :disabled="saving || !canAttemptConfirm(selected)"
            @click="confirmOrder"
          >
            {{ saving ? 'Procesando…' : 'Confirmar pedido' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useLoading } from "vue-loading-overlay";
import { useToast } from "vue-toast-notification";
import { store } from "../store.js";
import { findUniqueEmailClient } from "./utils/shopifyClients.js";

const loadingOverlay = useLoading({
  color: "#428b74",
  loader: "spinner",
});

export default {
  name: "ProveedorShopify",
  setup() {
    const route = useRoute();
    const orders = ref([]);
    const clients = ref([]);
    const products = ref([]);
    const storeInfo = ref(null);
    const storeDomain = ref("");
    const webhookSecret = ref("");
    const selected = ref(null);
    const clientSelection = ref(null);
    const suggestedClient = ref(null);
    const newClient = ref(emptyClient());
    const loading = ref(true);
    const saving = ref(false);
    const reconfiguring = ref(false);
    const editingStoreDomain = ref(false);
    const editingWebhookSecret = ref(false);
    const tab = ref("all");
    const tabs = [
      { value: "all", label: "Todos" },
      { value: "pending", label: "Pendientes" },
      { value: "confirmed", label: "Confirmadas" },
      { value: "discarded", label: "Descartadas" },
    ];

    const providerAuthorized = computed(() => {
      const provider = store.user;
      const correctProvider =
        provider &&
        provider.type === "proveedor" &&
        String(provider.id) === String(route.params.proveedor_id);

      if (!correctProvider) return false;
      return store.auth;
    });
    const webhookUrl = computed(() =>
      `${window.location.origin}/api/shopify/webhooks/orders`
    );
    const storeConfigured = computed(() =>
      Boolean(storeInfo.value && storeInfo.value.webhook_configured)
    );

    const filteredOrders = computed(() =>
      orders.value.filter((order) => {
        if (tab.value === "all") return true;
        if (tab.value === "pending") {
          return ["pending", "failed"].includes(order.status);
        }
        return order.status === tab.value;
      })
    );

    const readonly = computed(() =>
      selected.value ? ["confirmed", "discarded"].includes(selected.value.status) : true
    );

    const suggestionAssigned = computed(() =>
      Boolean(
        suggestedClient.value &&
          String(clientSelection.value) === String(suggestedClient.value.id)
      )
    );

    const contact = computed(() => {
      if (!selected.value) return {};
      return selected.value.datos_entrega.contacto;
    });

    async function loadInbox(keepSelectedId, showSpinner = true) {
      loading.value = true;
      const loader = showSpinner ? loadingOverlay.show() : null;
      try {
        const response = await axios.get("/api/shopify-inbox", {
          params: { proveedor_id: route.params.proveedor_id },
        });
        orders.value = response.data.orders;
        clients.value = response.data.clients;
        products.value = response.data.products;
        storeInfo.value = response.data.store;
        if (storeInfo.value) storeDomain.value = storeInfo.value.shop_domain;

        const requestedOrderId = keepSelectedId || route.query.order;
        const sameOrder =
          requestedOrderId &&
          filteredOrders.value.find((order) => order.id === requestedOrderId);
        selectOrder(sameOrder || filteredOrders.value[0] || null);
      } catch (requestError) {
        if (!requestError.response || requestError.response.status !== 401) {
          useToast().error(getError(requestError), { position: "top-right" });
        }
      } finally {
        if (loader) loader.hide();
        loading.value = false;
      }
    }

    function selectOrder(order) {
      selected.value = order ? JSON.parse(JSON.stringify(order)) : null;
      if (selected.value) {
        selected.value.datos_entrega = selected.value.datos_entrega || {};
        selected.value.datos_entrega.direccion = selected.value.datos_entrega.direccion || {};
        selected.value.datos_entrega.contacto = selected.value.datos_entrega.contacto || {};
      }
      clientSelection.value =
        selected.value && selected.value.cliente
          ? selected.value.cliente
          : null;
      suggestedClient.value =
        selected.value && !selected.value.cliente
          ? findUniqueEmailClient(selected.value.email, clients.value)
          : null;
      newClient.value = clientFromOrder(selected.value);
    }

    function assignSuggestedClient() {
      if (!selected.value || !suggestedClient.value || readonly.value) return;
      clientSelection.value = suggestedClient.value.id;
      selected.value.cliente = suggestedClient.value.id;
    }

    function selectClientOption() {
      if (!selected.value || readonly.value) return;
      if (clientSelection.value === "new") {
        selected.value.cliente = null;
        newClient.value = clientFromOrder(selected.value);
      } else {
        selected.value.cliente = clientSelection.value;
      }
    }

    async function connectStore() {
      if (
        !storeDomain.value ||
        (!storeConfigured.value && !webhookSecret.value) ||
        saving.value
      ) return;

      const wasConfigured = storeConfigured.value;
      saving.value = true;
      try {
        const response = await axios.post(
          "/api/shopify-inbox",
          {
            action: "connect_store",
            shop_domain: storeDomain.value,
            webhook_secret: webhookSecret.value,
          },
          {
            params: { proveedor_id: route.params.proveedor_id },
          }
        );
        storeInfo.value = response.data.store;
        storeDomain.value = response.data.store.shop_domain;
        webhookSecret.value = "";
        editingStoreDomain.value = false;
        editingWebhookSecret.value = false;
        reconfiguring.value = false;
        useToast().success(wasConfigured ? "Configuración de Shopify actualizada" : "Tienda de Shopify conectada", {
          position: "top-right",
        });
        await loadInbox();
      } catch (requestError) {
        useToast().error(getError(requestError), { position: "top-right" });
      } finally {
        saving.value = false;
      }
    }

    function beginReconfiguration() {
      webhookSecret.value = "";
      editingStoreDomain.value = false;
      editingWebhookSecret.value = false;
      reconfiguring.value = true;
    }

    function cancelReconfiguration() {
      if (storeInfo.value) storeDomain.value = storeInfo.value.shop_domain;
      webhookSecret.value = "";
      editingStoreDomain.value = false;
      editingWebhookSecret.value = false;
      reconfiguring.value = false;
    }

    function beginDomainEdit() {
      webhookSecret.value = "";
      editingWebhookSecret.value = false;
      editingStoreDomain.value = true;
    }

    function cancelDomainEdit() {
      if (storeInfo.value) storeDomain.value = storeInfo.value.shop_domain;
      editingStoreDomain.value = false;
    }

    function beginSecretEdit() {
      if (storeInfo.value) storeDomain.value = storeInfo.value.shop_domain;
      editingStoreDomain.value = false;
      webhookSecret.value = "";
      editingWebhookSecret.value = true;
    }

    function cancelSecretEdit() {
      webhookSecret.value = "";
      editingWebhookSecret.value = false;
    }

    function draftPayload() {
      return {
        id: selected.value.id,
        cliente: selected.value.cliente,
        instrucciones: selected.value.instrucciones,
        require_factura: selected.value.require_factura,
        datos_entrega: selected.value.datos_entrega,
        items: selected.value.shopify_order_items.map((item) => ({
          id: item.id,
          producto: item.producto,
          final_quantity: item.final_quantity,
          final_price: item.final_price,
          remember_mapping: item.remember_mapping,
        })),
      };
    }

    async function saveOrderDraft() {
      try {
        await axios.put("/api/shopify-inbox", draftPayload(), {
          params: { proveedor_id: route.params.proveedor_id },
        });
        return true;
      } catch (requestError) {
        useToast().error(getError(requestError), { position: "top-right" });
        return false;
      }
    }

    async function confirmOrder() {
      if (saving.value) return;
      if (clientSelection.value === "new" && !hasNewClientDetails(newClient.value)) {
        window.alert("Completa el nombre, la dirección y el contacto del cliente.");
        return;
      }

      saving.value = true;
      const loader = loadingOverlay.show();
      try {
        if (!(await saveOrderDraft())) return;
        const response = await axios.post(
          "/api/shopify-inbox",
          {
            id: selected.value.id,
            action: "confirm",
            client: clientSelection.value === "new" ? newClient.value : null,
          },
          { params: { proveedor_id: route.params.proveedor_id } }
        );
        useToast().success(`Pedido #${response.data.pedido} creado`, { position: "top-right" });
        await loadInbox(selected.value.id, false);
      } catch (requestError) {
        useToast().error(getError(requestError), { position: "top-right" });
        await loadInbox(selected.value.id, false);
      } finally {
        loader.hide();
        saving.value = false;
      }
    }

    async function discardOrder() {
      if (saving.value) return;
      const confirmed = window.confirm(
        "Descartar esta orden no creará una entrega en PickPack.\n\n¿Quieres continuar?"
      );
      if (!confirmed) return;

      saving.value = true;
      const loader = loadingOverlay.show();
      try {
        await axios.post(
          "/api/shopify-inbox",
          { id: selected.value.id, action: "discard" },
          { params: { proveedor_id: route.params.proveedor_id } }
        );
        const discardedOrder = orders.value.find(
          (order) => order.id === selected.value.id
        );
        if (discardedOrder) discardedOrder.status = "discarded";
        selectOrder(filteredOrders.value[0] || null);
        useToast().success("Orden descartada", { position: "top-right" });
      } catch (requestError) {
        useToast().error(getError(requestError), { position: "top-right" });
      } finally {
        loader.hide();
        saving.value = false;
      }
    }

    function canAttemptConfirm(order) {
      const creatingClient = Boolean(
        selected.value &&
        order &&
        selected.value.id === order.id &&
        clientSelection.value === "new"
      );
      return Boolean(
        order &&
        (order.cliente || creatingClient) &&
        hasDeliveryAddress(order) &&
        order.shopify_order_items &&
        order.shopify_order_items.length > 0 &&
        order.shopify_order_items.every(
          (item) => item.producto && Number(item.final_quantity) > 0 && Number(item.final_price) >= 0
        )
      );
    }

    function hasNewClientDetails(client) {
      if (!client || !client.direccion || !client.contacto) return false;
      return [
        client.nombre,
        client.direccion.calle,
        client.direccion.colonia,
        client.direccion.codigo,
        client.direccion.estado,
        client.contacto.nombre,
        client.contacto.telefono,
      ].every((value) => String(value == null ? "" : value).trim());
    }

    function hasDeliveryAddress(order) {
      const delivery = order && order.datos_entrega && order.datos_entrega.direccion;
      return Boolean(
        delivery &&
        ["calle", "colonia", "codigo", "estado"].every((field) =>
          String(delivery[field] == null ? "" : delivery[field]).trim()
        )
      );
    }

    function deliveryAddressLine(order) {
      const delivery = order && order.datos_entrega && order.datos_entrega.direccion;
      if (!delivery) return "Sin dirección";
      const street = [delivery.calle, delivery.exterior].filter(Boolean).join(" ");
      return [
        street,
        delivery.interior,
        delivery.colonia,
        [delivery.codigo, delivery.estado].filter(Boolean).join(" "),
      ]
        .filter(Boolean)
        .join(", ") || "Sin dirección";
    }

    function clientFromOrder(order) {
      if (!order) return emptyClient();
      const delivery = order.datos_entrega || {};
      const deliveryAddress = delivery.direccion || {};
      const deliveryContact = delivery.contacto || {};
      const name = order.customer_name || deliveryContact.nombre || "";
      return {
        nombre: name,
        direccion: {
          calle: deliveryAddress.calle || "",
          exterior: deliveryAddress.exterior || "",
          interior: deliveryAddress.interior || "",
          colonia: deliveryAddress.colonia || "",
          codigo: deliveryAddress.codigo || "",
          estado: deliveryAddress.estado || "",
        },
        contacto: {
          nombre: deliveryContact.nombre || name,
          telefono: deliveryContact.telefono || "",
          correo: order.email || "",
        },
      };
    }

    function emptyClient() {
      return {
        nombre: "",
        direccion: {
          calle: "",
          exterior: "",
          interior: "",
          colonia: "",
          codigo: "",
          estado: "",
        },
        contacto: { nombre: "", telefono: "", correo: "" },
      };
    }

    function orderStatusLabel(status) {
      if (status === "confirmed") return "Confirmado";
      if (status === "discarded") return "Descartado";
      return "Pendiente";
    }

    function orderStatusClass(status) {
      if (status === "confirmed") return "text-primary";
      if (status === "discarded") return "text-gray-500";
      return "text-yellow-700";
    }

    function countForTab(value) {
      if (value === "all") return orders.value.length;
      if (value === "pending") {
        return orders.value.filter((order) =>
          ["pending", "failed"].includes(order.status)
        ).length;
      }
      return orders.value.filter((order) => order.status === value).length;
    }

    function money(value, currency) {
      return new Intl.NumberFormat("es-MX", { style: "currency", currency: currency || "MXN" }).format(value || 0);
    }

    function date(value) {
      return value ? new Date(value).toLocaleDateString("es-MX", { timeZone: "America/Mexico_City" }) : "";
    }

    function getError(requestError) {
      return requestError.response && requestError.response.data && requestError.response.data.error
        ? requestError.response.data.error
        : "Hubo un error. Intenta nuevamente.";
    }

    async function copyWebhook() {
      await navigator.clipboard.writeText(webhookUrl.value);
      useToast().success("Webhook copiado", { position: "top-right" });
    }

    watch(providerAuthorized, (authorized) => {
      if (authorized) loadInbox(selected.value && selected.value.id);
    });
    watch(tab, () => selectOrder(filteredOrders.value[0] || null));
    onMounted(() => {
      if (providerAuthorized.value) loadInbox();
    });

    return {
      route,
      clients,
      products,
      storeInfo,
      storeDomain,
      storeConfigured,
      webhookSecret,
      providerAuthorized,
      selected,
      clientSelection,
      suggestedClient,
      suggestionAssigned,
      newClient,
      loading,
      saving,
      reconfiguring,
      editingStoreDomain,
      editingWebhookSecret,
      tab,
      tabs,
      filteredOrders,
      readonly,
      contact,
      webhookUrl,
      selectOrder,
      selectClientOption,
      assignSuggestedClient,
      connectStore,
      beginReconfiguration,
      cancelReconfiguration,
      beginDomainEdit,
      cancelDomainEdit,
      beginSecretEdit,
      cancelSecretEdit,
      confirmOrder,
      discardOrder,
      canAttemptConfirm,
      hasDeliveryAddress,
      hasNewClientDetails,
      deliveryAddressLine,
      orderStatusLabel,
      orderStatusClass,
      countForTab,
      money,
      date,
      copyWebhook,
    };
  },
};
</script>
